import { useState, type FormEvent } from 'react'
import { KIND_META, GROUP_LABELS, GROUP_ORDER } from './stationMarkerKinds'
import type { StationMarker, StationMarkerKind } from '../types'

// Create / edit modal for a StationMarker. Same one-form-two-callers
// pattern as ActionEditor / PlanTaskEditor. `mode='create'` when the
// user clicks an empty spot on the map (parent passes the click's
// normalised x/y as the initial position via `initialPosition`).

interface EditorBody {
  kind: StationMarkerKind
  label: string
  description: string | null
}

interface Props {
  mode: 'create' | 'edit'
  initial: StationMarker | null
  initialPosition?: { x: number; y: number }    // create-mode only
  saving: boolean
  deleting?: boolean
  serverError: Error | null
  onClose: () => void
  onSave: (body: EditorBody) => void
  onDelete?: () => void
}

export function StationMarkerEditor({
  mode, initial, saving, deleting, serverError,
  onClose, onSave, onDelete
}: Props) {
  // create-mode default: 'staff-post' (most common operational pin)
  const [kind, setKind] = useState<StationMarkerKind>(initial?.kind ?? 'staff-post')
  const [label, setLabel] = useState(initial?.label ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')

  const trimmedLabel = label.trim()
  const labelError =
    trimmedLabel.length === 0 ? 'Label is required'
    : trimmedLabel.length > 120 ? 'Label must be 120 characters or fewer'
    : null
  const isValid = labelError === null

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    onSave({
      kind,
      label: trimmedLabel,
      description: description.trim() || null
    })
  }

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-xl my-8 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">
            {mode === 'create' ? 'Place marker' : 'Edit marker'}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink text-xl">×</button>
        </div>

        <div className="space-y-3">
          {/* Kind picker grouped by category. Native select keeps it
              keyboard-friendly and lets the user see every kind at a
              glance — 18 options is enough that a custom popover would
              add weight without much benefit. */}
          <Label text="Kind">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as StationMarkerKind)}
              className={inputClass(false)}
              disabled={saving}
            >
              {GROUP_ORDER.map((g) => (
                <optgroup key={g} label={GROUP_LABELS[g]}>
                  {(Object.entries(KIND_META) as Array<[StationMarkerKind, typeof KIND_META[StationMarkerKind]]>)
                    .filter(([, meta]) => meta.group === g)
                    .map(([k, meta]) => (
                      <option key={k} value={k}>{meta.label}</option>
                    ))
                  }
                </optgroup>
              ))}
            </select>
          </Label>

          <Label text="Label" error={labelError}>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={120}
              autoFocus
              required
              className={inputClass(!!labelError)}
              placeholder="e.g. South platform first-aid"
            />
          </Label>

          <Label text="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              className={inputClass(false)}
              placeholder="Detail useful to whoever finds this on the map — coverage hours, comms channel, kit count, etc."
            />
          </Label>

          {serverError && (
            <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
              {serverError.message}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
          {mode === 'edit' && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving || deleting}
              className="mr-auto px-4 py-2 rounded text-sm font-semibold text-oxblood hover:bg-oxblood-soft disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || saving}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Place marker' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Label({ text, error, children }: { text: string; error?: string | null; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
        {text}
      </span>
      {children}
      {error && <div className="text-xs text-oxblood mt-1">{error}</div>}
    </label>
  )
}

function inputClass(invalid: boolean): string {
  return (
    'w-full border rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 transition-colors ' +
    (invalid
      ? 'border-oxblood focus:border-oxblood focus:ring-oxblood/30'
      : 'border-line focus:border-green focus:ring-green/30')
  )
}
